/**
 * Mónica · Centro de Aprendizaje (Admin)
 * 3 pestañas:
 *  1) Conocimiento — corrige / añade datos al system prompt sin tocar código
 *  2) Conversaciones — historial de chats + lead score + transcripción
 *  3) Feedback — 👍 / 👎 sobre respuestas individuales
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminFetch, adminMe, logoutAdmin } from '../lib/admin';
import './ConciergeKnowledge.css';

const CLASSIFICATIONS = [
  { key: 'all', label: 'Todos' },
  { key: 'caliente', label: '🔥 Caliente' },
  { key: 'tibio', label: 'Tibio' },
  { key: 'frio', label: 'Frío' },
  { key: 'contacted', label: 'Con contacto' },
];

function ScorePill({ score, classification }) {
  return (
    <span className={`ck-score ${classification}`} data-testid={`score-${classification}`}>
      <span className="ck-score-dot" />
      {score}/5 · {classification.toUpperCase()}
    </span>
  );
}

function SessionDrawer({ sessionId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const res = await adminFetch(`/api/concierge/sessions/${sessionId}`);
      if (alive && res?.ok) {
        setData(await res.json());
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [sessionId]);

  const scoring = data?.lead_scoring;

  return (
    <>
      <div className="ck-drawer-bg" onClick={onClose} />
      <aside className="ck-drawer" data-testid="ck-drawer">
        <header className="ck-drawer-head">
          <div>
            <h3>Transcripción de sesión</h3>
            <span className="ck-session-id">{sessionId}</span>
          </div>
          <button onClick={onClose} className="ck-drawer-close" aria-label="Cerrar" data-testid="ck-drawer-close">×</button>
        </header>
        <div className="ck-drawer-body">
          {loading && <p className="ck-empty">Cargando transcripción…</p>}
          {data && scoring && (
            <div className="ck-drawer-scoring">
              <strong>Lead Score:</strong> {scoring.score}/5 · {scoring.classification.toUpperCase()}
              {scoring.hot_signal_detected && ' · 🔥 SEÑAL DE COMPRA'}
              <br />
              <strong>Categorías cubiertas:</strong> {scoring.matched_categories.join(', ') || 'ninguna'}
              {(data.contact_name || data.contact_phone || data.contact_email) && (
                <>
                  <br />
                  <strong>Contacto:</strong> {[data.contact_name, data.contact_phone, data.contact_email].filter(Boolean).join(' · ')}
                </>
              )}
            </div>
          )}
          {data?.messages?.map((m, i) => (
            <div key={`${m.at || i}-${m.role}`} className={`ck-transcript-msg ${m.role}`}>
              <span className="ck-transcript-role">{m.role === 'user' ? 'Visitante' : 'Mónica'}</span>
              {m.content}
            </div>
          ))}
        </div>
      </aside>
    </>
  );
}

export default function ConciergeKnowledge() {
  const [items, setItems] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [promo, setPromo] = useState({ active: false, eyebrow: '', headline: '', detail: '', terms: '', cta_label: '', cta_url: '', align: 'left' });
  const [promoSaved, setPromoSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ title: '', content: '' });
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState('knowledge');
  const [sessionFilter, setSessionFilter] = useState('all');
  const [openSession, setOpenSession] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    adminMe().then(setMe).catch(() => navigate('/admin/login'));
  }, [navigate]);

  const refresh = useCallback(async () => {
    setLoading(true);
    const [kRes, fRes, sRes, pRes] = await Promise.all([
      adminFetch('/api/concierge/knowledge'),
      adminFetch('/api/concierge/feedback?limit=100'),
      adminFetch('/api/concierge/sessions?limit=100'),
      adminFetch('/api/promo/admin'),
    ]);
    if (kRes?.ok) setItems((await kRes.json()).items || []);
    if (fRes?.ok) setFeedback((await fRes.json()).items || []);
    if (sRes?.ok) setSessions((await sRes.json()).items || []);
    if (pRes?.ok) {
      const p = await pRes.json();
      setPromo({
        active: !!p.active,
        eyebrow: p.eyebrow || '',
        headline: p.headline || '',
        detail: p.detail || '',
        terms: p.terms || '',
        cta_label: p.cta_label || '',
        cta_url: p.cta_url || '',
        align: p.align || 'left',
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!me) return;
    let alive = true;
    (async () => {
      if (alive) await refresh();
    })();
    return () => { alive = false; };
  }, [me, refresh]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) return;
    if (editing) {
      await adminFetch(`/api/concierge/knowledge/${editing}`, {
        method: 'PATCH',
        body: JSON.stringify(form),
      });
    } else {
      await adminFetch('/api/concierge/knowledge', {
        method: 'POST',
        body: JSON.stringify({ ...form, active: true }),
      });
    }
    setForm({ title: '', content: '' });
    setEditing(null);
    refresh();
  };

  const handleEdit = (item) => {
    setEditing(item.id);
    setForm({ title: item.title, content: item.content });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggle = async (item) => {
    await adminFetch(`/api/concierge/knowledge/${item.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ active: !item.active }),
    });
    refresh();
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.title}"?`)) return;
    await adminFetch(`/api/concierge/knowledge/${item.id}`, { method: 'DELETE' });
    refresh();
  };

  const handlePromoSave = async (e) => {
    e.preventDefault();
    setPromoSaved(false);
    const res = await adminFetch('/api/promo/admin', {
      method: 'PUT',
      body: JSON.stringify(promo),
    });
    if (res?.ok) {
      setPromoSaved(true);
      setTimeout(() => setPromoSaved(false), 2500);
    }
  };

  const filteredSessions = sessions.filter((s) => {
    if (sessionFilter === 'all') return true;
    if (sessionFilter === 'contacted') return s.contact_name || s.contact_phone || s.contact_email;
    return s.lead_classification === sessionFilter;
  });

  const sessionStats = {
    total: sessions.length,
    caliente: sessions.filter((s) => s.lead_classification === 'caliente').length,
    contacted: sessions.filter((s) => s.contact_name || s.contact_phone || s.contact_email).length,
  };

  if (!me) return <div style={{ padding: '2rem' }}>Cargando…</div>;

  return (
    <div className="ck-page" data-testid="concierge-knowledge-page">
      <header className="ck-header">
        <div>
          <h1>Mónica · Centro de Aprendizaje</h1>
          <p className="ck-subtitle">{me.email} · Edita lo que Mónica sabe y revisa sus conversaciones.</p>
        </div>
        <div className="ck-header-actions">
          <Link to="/admin/lotes-editor" className="ck-btn ck-btn-ghost" data-testid="link-lotes">← Editor de lotes</Link>
          <button
            type="button"
            onClick={() => { logoutAdmin(); navigate('/admin/login'); }}
            className="ck-btn ck-btn-ghost"
            data-testid="ck-logout"
          >
            Cerrar sesión
          </button>
        </div>
      </header>

      <nav className="ck-tabs">
        <button
          className={`ck-tab ${tab === 'knowledge' ? 'active' : ''}`}
          onClick={() => setTab('knowledge')}
          data-testid="tab-knowledge"
        >
          Conocimiento ({items.filter((i) => i.active).length} activos)
        </button>
        <button
          className={`ck-tab ${tab === 'sessions' ? 'active' : ''}`}
          onClick={() => setTab('sessions')}
          data-testid="tab-sessions"
        >
          Conversaciones ({sessionStats.total}) · 🔥 {sessionStats.caliente} · ✉ {sessionStats.contacted}
        </button>
        <button
          className={`ck-tab ${tab === 'feedback' ? 'active' : ''}`}
          onClick={() => setTab('feedback')}
          data-testid="tab-feedback"
        >
          Feedback ({feedback.length})
        </button>
        <button
          className={`ck-tab ${tab === 'promo' ? 'active' : ''}`}
          onClick={() => setTab('promo')}
          data-testid="tab-promo"
        >
          Promo del hero {promo.active ? '· 🟢 activa' : '· ⚪ inactiva'}
        </button>
      </nav>

      {tab === 'knowledge' && (
        <>
          <section className="ck-form-card">
            <h2>{editing ? 'Editar entrada' : 'Añadir conocimiento o corrección'}</h2>
            <p className="ck-hint">
              Escribe en lenguaje natural lo que Mónica debe saber. Si hay datos previos en contradicción,
              <strong> esta información tiene prioridad</strong>. Sirve para corregir errores, agregar promociones,
              actualizar precios autorizados, listas de bancos, etc.
            </p>
            <form onSubmit={handleSave}>
              <label>
                <span>Título</span>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ej. Corrección sobre roof garden"
                  required
                  data-testid="ck-title"
                />
              </label>
              <label>
                <span>Contenido</span>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  placeholder="Ej. Ambos modelos (Fernanda y Mariana) tienen roof garden. Ambos parten de 3 recámaras con opción de 4ª."
                  rows={6}
                  required
                  data-testid="ck-content"
                />
              </label>
              <div className="ck-form-actions">
                <button type="submit" className="ck-btn ck-btn-primary" data-testid="ck-save">
                  {editing ? 'Guardar cambios' : 'Añadir a Mónica'}
                </button>
                {editing && (
                  <button
                    type="button"
                    onClick={() => { setEditing(null); setForm({ title: '', content: '' }); }}
                    className="ck-btn ck-btn-ghost"
                  >
                    Cancelar
                  </button>
                )}
              </div>
            </form>
          </section>

          <section className="ck-list">
            <h2>Entradas guardadas</h2>
            {loading && <p>Cargando…</p>}
            {!loading && items.length === 0 && (
              <p className="ck-empty">Aún no hay entradas. Añade la primera arriba.</p>
            )}
            {items.map((item) => (
              <article
                key={item.id}
                className={`ck-item ${item.active ? '' : 'inactive'}`}
                data-testid={`ck-item-${item.id}`}
              >
                <header>
                  <h3>{item.title}</h3>
                  <span className={`ck-badge ${item.active ? 'active' : 'inactive'}`}>
                    {item.active ? 'ACTIVO' : 'PAUSADO'}
                  </span>
                </header>
                <pre className="ck-content">{item.content}</pre>
                <footer>
                  <span className="ck-meta">
                    Por {item.created_by} · {new Date(item.created_at).toLocaleString('es-MX')}
                  </span>
                  <div className="ck-actions">
                    <button onClick={() => handleEdit(item)} className="ck-btn ck-btn-ghost">Editar</button>
                    <button onClick={() => handleToggle(item)} className="ck-btn ck-btn-ghost">
                      {item.active ? 'Pausar' : 'Activar'}
                    </button>
                    <button onClick={() => handleDelete(item)} className="ck-btn ck-btn-danger">Eliminar</button>
                  </div>
                </footer>
              </article>
            ))}
          </section>
        </>
      )}

      {tab === 'sessions' && (
        <section className="ck-list">
          <h2>Conversaciones recientes</h2>
          <p className="ck-hint">
            Cada tarjeta es una sesión real con Mónica. <strong>Click</strong> para ver la transcripción completa
            y el lead score detallado. Las sesiones con contacto ya están en GoHighLevel.
          </p>

          <div className="ck-sessions-toolbar">
            {CLASSIFICATIONS.map((c) => (
              <button
                key={c.key}
                className={`ck-filter-pill ${sessionFilter === c.key ? 'active' : ''}`}
                onClick={() => setSessionFilter(c.key)}
                data-testid={`filter-${c.key}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {loading && <p>Cargando…</p>}
          {!loading && filteredSessions.length === 0 && (
            <p className="ck-empty">Sin conversaciones para este filtro todavía.</p>
          )}

          {filteredSessions.map((s) => (
            <article
              key={s.session_id}
              className="ck-session-card"
              onClick={() => setOpenSession(s.session_id)}
              data-testid={`session-${s.session_id}`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') setOpenSession(s.session_id); }}
            >
              <div className="ck-session-head">
                <ScorePill score={s.lead_score} classification={s.lead_classification} />
                <span className="ck-session-id">{s.session_id.slice(0, 8)}…</span>
              </div>
              {s.last_user_message && (
                <p className="ck-session-snippet">&ldquo;{s.last_user_message}&rdquo;</p>
              )}
              <div className="ck-session-meta-row">
                <span>{s.user_messages_count} msgs visitante · {s.messages_count} total</span>
                <span>{new Date(s.updated_at || s.started_at).toLocaleString('es-MX')}</span>
                <span>{s.source_page}</span>
                {(s.contact_name || s.contact_phone || s.contact_email) && (
                  <span className="ck-session-contact" data-testid="session-contact-badge">
                    ✓ {s.contact_name || s.contact_phone || s.contact_email}
                  </span>
                )}
                {s.hot_signal && <span style={{ color: '#f6802c', fontWeight: 700 }}>🔥 señal de compra</span>}
              </div>
            </article>
          ))}
        </section>
      )}

      {tab === 'feedback' && (
        <section className="ck-list">
          <h2>Feedback de visitantes en el chat</h2>
          <p className="ck-hint">
            Aquí ves qué respuestas marcaron <strong>👍 buenas</strong> o <strong>👎 mejorables</strong> los visitantes.
            Usa esos datos para añadir correcciones en la pestaña &ldquo;Conocimiento&rdquo;.
          </p>
          {loading && <p>Cargando…</p>}
          {!loading && feedback.length === 0 && (
            <p className="ck-empty">Aún no hay feedback. Aparecerá aquí cuando los visitantes interactúen con Mónica.</p>
          )}
          {feedback.map((fb) => (
            <article key={`${fb.session_id}-${fb.message_index}-${fb.at}`} className="ck-feedback-item">
              <header>
                <span className={`ck-fb-rating ${fb.rating}`}>
                  {fb.rating === 'up' ? '👍 Buena' : '👎 Mejorable'}
                </span>
                <span className="ck-meta">{new Date(fb.at).toLocaleString('es-MX')}</span>
              </header>
              <p>
                <strong>Sesión:</strong> {fb.session_id?.slice(0, 8)}… ·{' '}
                <strong>Mensaje #</strong>{fb.message_index} ·{' '}
                <strong>Página:</strong> {fb.source_page || '/'}
              </p>
              {fb.comment && <p><em>&ldquo;{fb.comment}&rdquo;</em></p>}
              <button
                className="ck-btn ck-btn-ghost"
                onClick={() => setOpenSession(fb.session_id)}
                data-testid={`feedback-open-${fb.session_id}`}
              >
                Ver transcripción
              </button>
            </article>
          ))}
        </section>
      )}

      {tab === 'promo' && (
        <section className="ck-form-card" data-testid="promo-section">
          <h2>Promo del hero</h2>
          <p className="ck-hint">
            Aparece como una <strong>píldora naranja arriba del título</strong> en la página principal.
            Si la desactivas, el hero queda limpio (no rompe la estructura). Ideal para promociones
            temporales: descuentos, regalos por aparta, eventos abiertos, etc.
          </p>

          <div className="ck-promo-preview" aria-label="Vista previa">
            <div className="ck-promo-preview-label">VISTA PREVIA EN EL HERO</div>
            <div className="ck-promo-preview-stage">
              {promo.active && promo.headline ? (
                <span className="hero-promo-banner">
                  <span className="hero-promo-spark" aria-hidden>✦</span>
                  <span
                    className="hero-promo-text"
                    style={{
                      alignItems: promo.align === 'center' ? 'center' : promo.align === 'right' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    {promo.eyebrow ? (
                      <span className="hero-promo-eyebrow" style={{ textAlign: promo.align || 'left' }}>{promo.eyebrow}</span>
                    ) : null}
                    <strong style={{ textAlign: promo.align || 'left' }}>{promo.headline}</strong>
                    {promo.detail ? <span className="hero-promo-detail" style={{ textAlign: promo.align || 'left' }}>{promo.detail}</span> : null}
                  </span>
                  {promo.cta_url ? (
                    <span className="hero-promo-cta-pill">
                      {promo.cta_label || 'Ver más'}
                      <span className="hero-promo-arrow" aria-hidden>→</span>
                    </span>
                  ) : null}
                </span>
              ) : (
                <span className="ck-promo-empty-state">— Sin promo activa, no aparece en el hero —</span>
              )}
            </div>
          </div>

          <form onSubmit={handlePromoSave}>
            <label className="ck-promo-toggle">
              <input
                type="checkbox"
                checked={promo.active}
                onChange={(e) => setPromo({ ...promo, active: e.target.checked })}
                data-testid="promo-active"
              />
              <span>Mostrar en el hero</span>
            </label>

            <label>
              <span>Texto superior (opcional) <small style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, opacity: 0.7 }}>— línea pequeña arriba, tipo etiqueta</small></span>
              <input
                type="text"
                value={promo.eyebrow}
                onChange={(e) => setPromo({ ...promo, eyebrow: e.target.value })}
                placeholder="Ej. Solo este mes · Promoción de junio · Exclusivo web"
                maxLength={48}
                data-testid="promo-eyebrow"
              />
            </label>

            <label>
              <span>Texto principal * <small style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, opacity: 0.7 }}>— una sola línea, máx 60 caracteres</small></span>
              <input
                type="text"
                value={promo.headline}
                onChange={(e) => setPromo({ ...promo, headline: e.target.value })}
                placeholder="Ej. 10% de descuento en tu enganche"
                maxLength={60}
                required={promo.active}
                data-testid="promo-headline"
              />
            </label>

            <label>
              <span>Detalle (opcional)</span>
              <input
                type="text"
                value={promo.detail}
                onChange={(e) => setPromo({ ...promo, detail: e.target.value })}
                placeholder="Ej. Aplican condiciones · Solo hasta el 30 de junio"
                maxLength={80}
                data-testid="promo-detail"
              />
            </label>

            <label>
              <span>Términos y condiciones (opcional) <small style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, opacity: 0.7 }}>— se abre en un modal cuando el visitante hace click en el detalle</small></span>
              <textarea
                value={promo.terms}
                onChange={(e) => setPromo({ ...promo, terms: e.target.value })}
                placeholder={"Vigencia: del 1 al 30 de junio de 2026.\nAplica para los primeros 10 apartados.\nNo acumulable con otras promociones.\nKuru, S.A. de C.V. se reserva el derecho de modificar o cancelar la promoción sin previo aviso, respetando los apartados ya celebrados."}
                rows={8}
                data-testid="promo-terms"
                style={{ resize: 'vertical', fontFamily: 'inherit', minHeight: '160px' }}
              />
              <small style={{ fontWeight: 500, fontSize: '0.75rem', color: '#5a6678', marginTop: '0.3rem', display: 'block' }}>
                Cuando llenes este campo, el texto del &quot;Detalle&quot; se vuelve clickable (con ícono ⓘ) y abre un modal con estos términos. Si lo dejas vacío, el detalle queda como texto simple sin link.
              </small>
            </label>

            <div className="ck-align-row">
              <span className="ck-align-label">Alineación del texto</span>
              <div className="ck-align-group" role="radiogroup" aria-label="Alineación del texto">
                {[
                  { v: 'left',   label: 'Izquierda', icon: '⟸' },
                  { v: 'center', label: 'Centrado',  icon: '☰' },
                  { v: 'right',  label: 'Derecha',   icon: '⟹' },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    role="radio"
                    aria-checked={promo.align === opt.v}
                    className={`ck-align-btn ${promo.align === opt.v ? 'active' : ''}`}
                    onClick={() => setPromo({ ...promo, align: opt.v })}
                    data-testid={`promo-align-${opt.v}`}
                  >
                    <span className="ck-align-icon" aria-hidden>{opt.icon}</span>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="ck-promo-cta-row">
              <label>
                <span>CTA — texto (opcional)</span>
                <input
                  type="text"
                  value={promo.cta_label}
                  onChange={(e) => setPromo({ ...promo, cta_label: e.target.value })}
                  placeholder="Ej. Aprovéchala"
                  maxLength={32}
                  data-testid="promo-cta-label"
                />
              </label>
              <label>
                <span>CTA — link (opcional)</span>
                <input
                  type="text"
                  value={promo.cta_url}
                  onChange={(e) => setPromo({ ...promo, cta_url: e.target.value })}
                  placeholder="Ej. #agendar-visita o https://..."
                  data-testid="promo-cta-url"
                />
              </label>
            </div>
            <p className="ck-hint" style={{ marginTop: '-0.4rem' }}>
              Llena <strong>ambos</strong> campos del CTA si quieres que la promo sea clickable.
              Si dejas cualquiera vacío, se muestra como banner informativo sin link.
            </p>

            <div className="ck-form-actions">
              <button type="submit" className="ck-btn ck-btn-primary" data-testid="promo-save">
                Guardar promo
              </button>
              {promoSaved && (
                <span className="ck-promo-saved" data-testid="promo-saved">✓ Guardado · ya está en el sitio</span>
              )}
            </div>
          </form>
        </section>
      )}

      {openSession && (
        <SessionDrawer sessionId={openSession} onClose={() => setOpenSession(null)} />
      )}
    </div>
  );
}
